import React, { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { useTranslation } from "react-i18next";
import {
  FiMapPin,
  FiSearch,
  FiGlobe,
} from "react-icons/fi";
import { MdSatellite, MdMap, MdMyLocation } from "react-icons/md";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
// @ts-ignore
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
// @ts-ignore
import markerIcon from "leaflet/dist/images/marker-icon.png";
// @ts-ignore
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import "leaflet/dist/leaflet.css";

// Fix Leaflet icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const LeafletMapContainer = MapContainer as any;
const LeafletTileLayer = TileLayer as any;
const LeafletMarker = Marker as any;

const customIcon = new L.Icon({
  iconUrl:
    "data:image/svg+xml;base64," +
    btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#020637" width="36" height="36">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  `),
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  shadowSize: [41, 41],
  shadowAnchor: [12, 41],
});

export interface Position {
  lat: number;
  lng: number;
}

export const DEFAULT_LOCATION: Position = { lat: 40.7128, lng: -74.006 }; // New York, USA

interface MapUpdaterProps {
  position: Position | null;
}

function MapUpdater({ position }: MapUpdaterProps) {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.setView(position, map.getZoom());
    }
  }, [position, map]);

  return null;
}

interface MapEventsProps {
  setPosition: (position: Position | null) => void;
}

function MapEvents({ setPosition }: MapEventsProps) {
  useMapEvents({
    click(e) {
      setPosition({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

interface SearchControlProps {
  currentAddress: string;
  onSearch: (position: Position, searchResult: any) => void;
  onUseCurrentLocation: (position: Position) => void;
}

function SearchControl({ currentAddress, onSearch, onUseCurrentLocation }: SearchControlProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearchingSuggestions, setIsSearchingSuggestions] = useState(false);
  const [lastSelectedName, setLastSelectedName] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (currentAddress) {
      setSearchQuery(currentAddress);
      setLastSelectedName(currentAddress);
    }
  }, [currentAddress]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchQuery === lastSelectedName) {
      return;
    }

    if (searchQuery.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setIsSearchingSuggestions(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            searchQuery
          )}&limit=5&addressdetails=1`
        );
        if (response.ok) {
          const results = await response.json();
          setSuggestions(results);
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error("Autocomplete fetch error:", error);
      } finally {
        setIsSearchingSuggestions(false);
      }
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, lastSelectedName]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          searchQuery
        )}&limit=5&addressdetails=1`
      );

      if (!response.ok) {
        throw new Error("Search failed");
      }

      const results = await response.json();

      if (results.length > 0) {
        const firstResult = results[0];
        const position = {
          lat: parseFloat(firstResult.lat),
          lng: parseFloat(firstResult.lon),
        };

        setLastSelectedName(firstResult.display_name);
        setSearchQuery(firstResult.display_name);
        setSuggestions([]);
        setShowSuggestions(false);
        onSearch(position, firstResult);
      } else {
        toast.error(t("auth.errorLocationNotFound"));
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error(t("auth.errorSearchFailed"));
    } finally {
      setIsSearching(false);
    }
  };

  const handleUseCurrentLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userPosition = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setLastSelectedName("");
          setSearchQuery("");
          setSuggestions([]);
          setShowSuggestions(false);
          onUseCurrentLocation(userPosition);
          toast.success(t("auth.successLocationDetected"));
        },
        (error) => {
          console.error("Geolocation error:", error);
          toast.error(t("auth.errorGeolocationDenied"));
        },
      );
    } else {
      toast.error(t("auth.errorGeolocationUnsupported"));
    }
  };

  const handleSelectSuggestion = (suggestion: any) => {
    const position = {
      lat: parseFloat(suggestion.lat),
      lng: parseFloat(suggestion.lon),
    };
    setLastSelectedName(suggestion.display_name);
    setSearchQuery(suggestion.display_name);
    setSuggestions([]);
    setShowSuggestions(false);
    onSearch(position, suggestion);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="relative flex gap-2">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => {
              if (suggestions.length > 0) setShowSuggestions(true);
            }}
            onKeyPress={handleKeyPress}
            placeholder={t("settings.searchAddress")}
            className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-300 focus:border-[#020637] focus:ring-2 focus:ring-[#020637]/20 focus:ring-opacity-50 transition-all bg-white"
          />
          {isSearchingSuggestions && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-[#020637] border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleSearch}
          disabled={isSearching}
          className="h-10 px-4 bg-[#020637] text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
        >
          {isSearching ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>{t("settings.searching")}</span>
            </>
          ) : (
            <>
              <FiSearch className="w-4 h-4" />
              <span>{t("settings.search")}</span>
            </>
          )}
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleUseCurrentLocation}
          className="h-10 px-3 bg-gray-100 text-gray-700 rounded-lg font-medium flex items-center gap-2 hover:bg-gray-200 transition-colors"
          title="Use my current location"
        >
          <MdMyLocation className="w-4 h-4" />
        </motion.button>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto py-1">
          {suggestions.map((suggestion, index) => {
            const address = suggestion.address || {};
            const title = address.road || address.suburb || address.city || address.country || suggestion.display_name;
            return (
              <div
                key={suggestion.place_id || index}
                onClick={() => handleSelectSuggestion(suggestion)}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#020637]/8 cursor-pointer transition-colors border-b border-gray-50 last:border-0 text-left"
              >
                <FiMapPin className="w-4 h-4 text-[#020637] shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-semibold text-gray-800 truncate">
                    {title}
                  </span>
                  <span className="text-xs text-gray-400 truncate">
                    {suggestion.display_name}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface AddressDetails {
  country: string;
  city: string;
  address: string;
  state: string;
  postcode: string;
  fullAddress: string;
}

interface LocationSelectorProps {
  position: Position | null;
  setPosition: (position: Position | null) => void;
  setAddressDetails: React.Dispatch<React.SetStateAction<AddressDetails>>;
}

type MapStyleKey = "light" | "dark" | "satellite";

interface MapStyleConfig {
  url: string;
  icon: React.ReactNode;
}

export function LocationSelector({
  position,
  setPosition,
  setAddressDetails,
}: LocationSelectorProps) {
  const { t } = useTranslation();
  const [mapStyle, setMapStyle] = useState<MapStyleKey>("light");
  const [currentAddress, setCurrentAddress] = useState("");
  const [, setIsGeocoding] = useState(false);

  const mapStyles: Record<MapStyleKey, MapStyleConfig> = {
    light: {
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      icon: <MdMap className="w-4 h-4" />,
    },
    dark: {
      url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
      icon: <FiGlobe className="w-4 h-4" />,
    },
    satellite: {
      url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      icon: <MdSatellite className="w-4 h-4" />,
    },
  };

  const fetchAddressDetails = async (pos: Position) => {
    if (!pos) return;

    setIsGeocoding(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${pos.lat}&lon=${pos.lng}&format=json&addressdetails=1`
      );
      const data = await res.json();

      if (data && data.address) {
        const addr = data.address;
        const fullAddr = data.display_name || "";
        setCurrentAddress(fullAddr);
        setAddressDetails({
          country: addr.country || "",
          city: addr.city || addr.town || addr.village || "",
          address:
            addr.road || addr.house_number
              ? `${addr.road || ""} ${addr.house_number || ""}`.trim()
              : "",
          state: addr.state || addr.county || "",
          postcode: addr.postcode || "",
          fullAddress: fullAddr,
        });
      }
    } catch (error) {
      console.error("Reverse geocoding error:", error);
    } finally {
      setIsGeocoding(false);
    }
  };

  useEffect(() => {
    if (position) {
      fetchAddressDetails(position);
    } else {
      setPosition(DEFAULT_LOCATION);
    }
  }, [position]);

  const handleSearch = async (newPosition: Position, searchResult: any) => {
    setPosition(newPosition);

    if (searchResult) {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${newPosition.lat}&lon=${newPosition.lng}&format=json&addressdetails=1`
      );
      const data = await res.json();

      if (data && data.address) {
        const addr = data.address;
        const fullAddr = data.display_name || "";
        setCurrentAddress(fullAddr);
        setAddressDetails({
          country: addr.country || "",
          city: addr.city || addr.town || addr.village || "",
          address:
            addr.road || addr.house_number
              ? `${addr.road || ""} ${addr.house_number || ""}`.trim()
              : "",
          state: addr.state || addr.county || "",
          postcode: addr.postcode || "",
          fullAddress: fullAddr,
        });
      }
    }

    toast.success(t("auth.successLocationFound"));
  };

  const handleUseCurrentLocation = (userPosition: Position) => {
    setCurrentAddress(""); // Reset so SearchControl's useEffect always fires
    setPosition(userPosition);
    fetchAddressDetails(userPosition);
  };

  return (
    <motion.div
      initial={{ opacity: 1, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className="flex flex-col w-full md:w-[50%] h-full"
    >
      <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
        {t("settings.location")}
      </label>

      <div className="mb-4">
        <SearchControl
          currentAddress={currentAddress}
          onSearch={handleSearch}
          onUseCurrentLocation={handleUseCurrentLocation}
        />
      </div>

      {position && (
        <motion.div
          initial={{ opacity: 1, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="relative rounded-2xl overflow-hidden border-2 border-gray-200 shadow-2xl group z-0"
        >
          <div className="absolute top-4 left-4 z-10 flex gap-2">
            <div className="flex gap-1 bg-white/95 backdrop-blur-sm rounded-xl p-1 border border-gray-200 shadow-sm">
              {(Object.keys(mapStyles) as MapStyleKey[]).map((style) => (
                <motion.button
                  key={style}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setMapStyle(style)}
                  className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-xs font-medium transition-all ${
                    mapStyle === style
                      ? "bg-[#020637] text-white"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  {mapStyles[style].icon}
                  <span className="capitalize">
                    {t(`settings.${style}`)}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>

          <LeafletMapContainer
            center={position || DEFAULT_LOCATION}
            zoom={14}
            style={{
              height: "350px",
              width: "100%",
            }}
            className="rounded-2xl"
            scrollWheelZoom={true}
            attributionControl={false}
          >
            <LeafletTileLayer url={mapStyles[mapStyle].url} attribution="" />
            <MapUpdater position={position || DEFAULT_LOCATION} />
            <MapEvents setPosition={setPosition} />
            <LeafletMarker
              position={position || DEFAULT_LOCATION}
              icon={customIcon}
              draggable
              eventHandlers={{
                dragstart: () => {
                  document.body.style.cursor = "grabbing";
                },
                dragend: async (e: any) => {
                  document.body.style.cursor = "";
                  const newPosition = e.target.getLatLng();
                  setPosition(newPosition);
                  await fetchAddressDetails(newPosition);
                },
              }}
            />
          </LeafletMapContainer>

          <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-3 rounded-xl shadow-xl border border-gray-200">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#020637]/10 flex items-center justify-center">
                <FiMapPin className="w-4 h-4 text-[#020637]" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">
                  {t("settings.dragMarker")}
                </p>
                <p className="text-xs text-gray-500">
                  {t("settings.clickMap")}
                </p>
              </div>
            </div>
          </div>

          <div className="absolute top-4 right-4 bg-black/80 text-white px-3 py-2 rounded-lg text-xs font-mono backdrop-blur-sm">
            {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
